/**
 * Filter and form controls.
 *
 * WHY `Select` IS HAND-BUILT
 * --------------------------
 * The filter bar needs options that carry an icon and a right-aligned count, and
 * a panel that opens upward when the bar sits near the bottom of a short
 * viewport. A native <select> can do neither. The cost of that decision is that
 * everything a native select gives away for free has to be re-implemented here:
 * roles, arrow keys, type-ahead, Escape, outside click, and returning focus to
 * the trigger. It is implemented once, in this file. Do not start a second one.
 *
 * Every control here is CONTROLLED — it renders the value it is handed and
 * reports intent through its callback, holding no shadow copy. Two sources of
 * truth for a filter is exactly how a chip row ends up disagreeing with the
 * result count sitting next to it.
 */

import React, { useCallback, useEffect, useId, useRef, useState, useMemo} from 'react';
import { fmtInt } from '../../lib/format';
import { Button } from './Button';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Heights and type steps are lifted from Button deliberately: a select, a search
   box, and a button standing side by side in a filter bar must share a baseline,
   and a baseline derived from padding does not survive a font change. */
const SIZE = {
  sm: { box: 'h-7 text-micro', px: 'px-2' },
  md: { box: 'h-[34px] text-label', px: 'px-2.5' },
  lg: { box: 'h-10 text-body', px: 'px-3' },
};

const sizing = (size) => SIZE[size] || SIZE.md;

const FIELD =
  'rounded-md border border-line-strong bg-surface text-ink-900 transition-colors duration-fast';

/** FA class string or a ready-made node — the same icon contract as Button. */
const renderIcon = (icon, className) => {
  if (icon === null || icon === undefined || icon === false) return null;
  if (typeof icon !== 'string') return icon;
  const styled = /(^|\s)(fas|far|fab|fal|fad|fa-solid|fa-regular|fa-brands|fa-light)(\s|$)/.test(icon);
  return <i className={cx(styled ? icon : `fas ${icon}`, className)} aria-hidden="true" />;
};

/** Rendered only when the caller supplies text, so an unlabelled field stays tight. */
function FieldLabel({ id, htmlFor, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <label id={id} htmlFor={htmlFor} className="mb-1 block text-label font-medium text-ink-700">
      {children}
    </label>
  );
}

/* ─── Select ─────────────────────────────────────────────────────────────── */

/* Tall enough for eight rows. Used to decide whether the panel fits below the
   trigger before it has been measured — the panel does not exist yet at that
   point, so an estimate is the only thing available. */
const PANEL_MAX_PX = 264;
const ROW_PX = 32;

/** Option text for type-ahead. Falls back to the value when the label is a node. */
const optionText = (opt) =>
  typeof opt?.label === 'string' ? opt.label : String(opt?.value ?? '');

/** First option at or after `from` (wrapping) whose label starts with `query`. */
const matchFrom = (list, query, from) => {
  const n = list.length;
  for (let i = 0; i < n; i += 1) {
    const k = (from + i + n) % n;
    if (optionText(list[k]).toLowerCase().startsWith(query)) return k;
  }
  return -1;
};

const isTypeahead = (event) =>
  event.key.length === 1 && event.key !== ' ' && !event.metaKey && !event.ctrlKey && !event.altKey;

/**
 * A select-only combobox over a real listbox.
 *
 * @param {object}   props
 * @param {*}        props.value        currently selected option value
 * @param {Function} props.onChange     (value) => void
 * @param {Array}    props.options      [{ value, label, icon?, meta? }]
 * @param {string}   [props.placeholder] shown when nothing is selected
 * @param {string}   [props.label]      visible label; also the accessible name
 * @param {'sm'|'md'|'lg'} [props.size]
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  label,
  size = 'md',
  disabled = false,
  id,
  className = '',
}) {
  const auto = useId();
  const selectId = id || `select-${auto}`;
  const listId = `${selectId}-listbox`;
  const labelId = `${selectId}-label`;
  const optionId = (i) => `${selectId}-option-${i}`;

  // Memoised: `filter` allocates a new array every render, which would make the
  // keyboard handlers below re-create on every keystroke.
  const list = useMemo(() => (Array.isArray(options) ? options.filter(Boolean) : []), [options]);
  const selectedIndex = list.findIndex((opt) => opt.value === value);
  const selected = selectedIndex >= 0 ? list[selectedIndex] : null;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, selectedIndex));
  const [dropUp, setDropUp] = useState(false);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const typed = useRef({ buffer: '', at: 0 });

  const step = sizing(size);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const wanted = Math.min(PANEL_MAX_PX, Math.max(2 * ROW_PX, list.length * ROW_PX + 10));
    const below = window.innerHeight - rect.bottom;
    // Inside a scrolling body (a modal), a panel opening upward is clipped at the
    // body's top edge, while one opening downward just lengthens the scroll area.
    // So there, flip up only when the whole panel fits above within that body.
    let scroller = el.parentElement;
    while (scroller && scroller !== document.body) {
      const overflowY = window.getComputedStyle(scroller).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scroller = scroller.parentElement;
    }
    if (scroller && scroller !== document.body) {
      const aboveInBody = rect.top - Math.max(0, scroller.getBoundingClientRect().top);
      setDropUp(below < wanted + 8 && aboveInBody >= wanted + 8);
      return;
    }
    // Flip only when there is genuinely no room below AND more room above, so a
    // select in the middle of a long page never flips back and forth on scroll.
    setDropUp(below < wanted + 8 && rect.top > below);
  }, [list.length]);

  const openPanel = useCallback(
    (index) => {
      if (disabled) return;
      setActiveIndex(typeof index === 'number' ? index : Math.max(0, selectedIndex));
      place();
      setOpen(true);
    },
    [disabled, place, selectedIndex],
  );

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    typed.current = { buffer: '', at: 0 };
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index, returnFocus = true) => {
      const opt = list[index];
      if (opt && onChange) onChange(opt.value);
      close(returnFocus);
    },
    [close, list, onChange],
  );

  // Outside click closes; scroll and resize re-decide which way the panel opens,
  // because the trigger moves under a sticky filter bar.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onReflow = () => place();
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, place]);

  // Keep the active option in view. Focus never leaves the trigger, so the
  // browser will not do this for us.
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(optionId(activeIndex));
    if (el) el.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex]);

  const jump = (char) => {
    const now = Date.now();
    const state = typed.current;
    state.buffer = now - state.at > 600 ? char : state.buffer + char;
    state.at = now;
    // A repeated single character cycles through matches; a longer buffer
    // re-searches from the current position so refining never skips ahead.
    const from = state.buffer.length > 1 ? activeIndex : activeIndex + 1;
    return matchFrom(list, state.buffer.toLowerCase(), from);
  };

  const onKeyDown = (event) => {
    if (disabled) return;

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        openPanel();
        return;
      }
      if (isTypeahead(event) && list.length) {
        event.preventDefault();
        const hit = jump(event.key);
        openPanel(hit >= 0 ? hit : undefined);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (list.length) setActiveIndex((i) => (i + 1) % list.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (list.length) setActiveIndex((i) => (i - 1 + list.length) % list.length);
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(Math.max(0, list.length - 1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (list.length) commit(activeIndex);
        else close();
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        // Tab commits and lets focus continue to the next control, matching a
        // native select. No preventDefault, and no focus return.
        if (list.length) commit(activeIndex, false);
        else close(false);
        break;
      default:
        if (isTypeahead(event) && list.length) {
          event.preventDefault();
          const hit = jump(event.key);
          if (hit >= 0) setActiveIndex(hit);
        }
    }
  };

  return (
    <div className={cx('min-w-0', className)}>
      <FieldLabel id={labelId} htmlFor={selectId}>
        {label}
      </FieldLabel>
      <div ref={rootRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={selectId}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && list.length ? optionId(activeIndex) : undefined}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : placeholder}
          disabled={disabled}
          onClick={() => (open ? close() : openPanel())}
          onKeyDown={onKeyDown}
          className={cx(
            FIELD,
            step.box,
            step.px,
            'flex w-full items-center justify-between gap-2 text-left font-medium',
            open && 'border-brand-line',
            disabled
              ? 'cursor-not-allowed bg-surface-2 text-ink-400'
              : 'hover:bg-surface-2',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected ? renderIcon(selected.icon, 'text-ink-500') : null}
            <span className={cx('truncate', selected ? 'text-ink-900' : 'text-ink-500 font-normal')}>
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <i
            className={cx(
              'fas fa-chevron-down shrink-0 text-micro transition-transform duration-fast',
              disabled ? 'text-ink-400' : 'text-ink-500',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>

        {open && (
          /* Options are role="option" list items, not buttons: in a listbox the
             combobox keeps DOM focus and points at the active row with
             aria-activedescendant. Real buttons here would steal focus and break
             that relationship — the keyboard contract lives on the trigger. */
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={label ? labelId : undefined}
            className={cx(
              'absolute inset-x-0 z-30 max-h-64 overflow-y-auto rounded-md border border-line bg-surface py-1',
              'shadow-pop thin-scrollbar animate-fade-in',
              dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {list.length === 0 && (
              <li role="presentation" className="px-2.5 py-2 text-body text-ink-500">
                No options
              </li>
            )}
            {list.map((opt, i) => {
              const isSelected = i === selectedIndex;
              return (
                <li
                  key={String(opt.value)}
                  id={optionId(i)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setActiveIndex(i)}
                  onClick={() => commit(i)}
                  className={cx(
                    'flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-body',
                    i === activeIndex ? 'bg-surface-2' : '',
                    isSelected ? 'font-medium text-ink-900' : 'text-ink-700',
                  )}
                >
                  {renderIcon(opt.icon, 'w-4 shrink-0 text-center text-ink-500')}
                  <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                  {opt.meta !== undefined && opt.meta !== null && (
                    <span className="shrink-0 font-mono text-micro tabular-nums text-ink-500">
                      {opt.meta}
                    </span>
                  )}
                  {isSelected && <i className="fas fa-check shrink-0 text-micro text-brand" aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ─── Chips ──────────────────────────────────────────────────────────────── */

const CHIP_TONE = {
  neutral: 'border-line bg-surface-2 text-ink-700',
  brand: 'border-brand-line bg-brand-tint text-brand',
  good: 'border-good-line bg-good-tint text-good-text',
  warn: 'border-warn-line bg-warn-tint text-warn-text',
  bad: 'border-bad-line bg-bad-tint text-bad-text',
};

/**
 * One applied filter.
 *
 * @param {object}   props
 * @param {*}        props.label      what the filter is, e.g. "Agent: Priya"
 * @param {Function} [props.onRemove] omit for a read-only chip — no dead X
 * @param {'neutral'|'brand'|'good'|'warn'|'bad'} [props.tone]
 * @param {string}   [props.title]    full text when the label is truncated
 */
export function Chip({ label, onRemove, tone = 'neutral', title, className = '' }) {
  const text = typeof label === 'string' ? label : null;
  return (
    <span
      title={title || text || undefined}
      className={cx(
        'inline-flex max-w-full items-center gap-1 rounded-sm border px-1.5 py-0.5 text-micro',
        CHIP_TONE[tone] || CHIP_TONE.neutral,
        className,
      )}
    >
      <span className="truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={text ? `Remove filter: ${text}` : 'Remove filter'}
          className="-mr-0.5 shrink-0 rounded-sm px-0.5 leading-none hover:bg-surface-3"
        >
          <i className="fas fa-xmark text-micro" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

/**
 * The applied-filter row that sits under a filter bar.
 *
 * `resultCount` is omitted entirely when it is not a known number: "0 calls"
 * under a failed or in-flight request is a lie, and this row is read as the
 * summary of what the table below is showing.
 *
 * @param {object}   props
 * @param {Array}    props.chips        [{ key, label, tone? }]
 * @param {Function} props.onRemove     (key) => void — must work for every chip,
 *                                      the date chip included
 * @param {Function} [props.onClear]    clears all of them at once
 * @param {number}   [props.resultCount]
 * @param {string}   [props.resultNoun]
 */
export function ChipRow({ chips, onRemove, onClear, resultCount, resultNoun = 'calls', className = '' }) {
  const list = Array.isArray(chips) ? chips.filter(Boolean) : [];
  const hasCount = typeof resultCount === 'number' && Number.isFinite(resultCount);
  if (!list.length && !hasCount) return null;

  return (
    <div className={cx('flex flex-wrap items-center gap-1.5', className)}>
      {list.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          tone={chip.tone}
          title={chip.title}
          onRemove={onRemove ? () => onRemove(chip.key) : undefined}
        />
      ))}

      {list.length > 0 && onClear && (
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear all
        </Button>
      )}

      {hasCount && (
        <span className="ml-auto shrink-0 text-micro text-ink-500" aria-live="polite">
          <span className="font-mono font-medium tabular-nums text-ink-700">{fmtInt(resultCount)}</span>{' '}
          {resultNoun}
        </span>
      )}
    </div>
  );
}

/* ─── SegmentedControl ───────────────────────────────────────────────────── */

/**
 * A small set of mutually exclusive choices, all visible at once. Two to four
 * options; past that use a Select — a segmented control that wraps onto a second
 * line stops reading as one control.
 *
 * Implemented as a radiogroup with a roving tabindex, so the whole group is one
 * tab stop and the arrow keys move between options exactly as they do in a
 * native radio group.
 *
 * @param {object}   props
 * @param {*}        props.value
 * @param {Function} props.onChange  (value) => void
 * @param {Array}    props.options   [{ value, label, icon?, meta? }]
 * @param {string}   [props.label]   the accessible name for the group
 */
export function SegmentedControl({ value, onChange, options, size = 'sm', label, className = '' }) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const refs = useRef([]);
  const step = sizing(size);
  const selectedIndex = list.findIndex((opt) => opt.value === value);
  // With nothing selected the first option carries the tab stop, so the group is
  // always reachable from the keyboard.
  const tabIndexAt = (i) => (i === (selectedIndex >= 0 ? selectedIndex : 0) ? 0 : -1);

  const onKeyDown = (event, i) => {
    const n = list.length;
    if (!n) return;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % n;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + n) % n;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = n - 1;
    if (next === null) return;
    event.preventDefault();
    if (onChange) onChange(list[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('inline-flex max-w-full gap-0.5 rounded-md border border-line bg-surface-2 p-0.5', className)}
    >
      {list.map((opt, i) => {
        const isSelected = i === selectedIndex;
        return (
          <button
            key={String(opt.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={tabIndexAt(i)}
            onClick={() => onChange && onChange(opt.value)}
            onKeyDown={(event) => onKeyDown(event, i)}
            className={cx(
              'inline-flex min-w-0 items-center justify-center gap-1.5 rounded-sm border px-2.5 font-medium',
              'whitespace-nowrap transition-colors duration-fast',
              step.box,
              isSelected
                ? 'border-line bg-surface text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-900',
            )}
          >
            {renderIcon(opt.icon, isSelected ? 'text-brand' : 'text-ink-400')}
            <span className="truncate">{opt.label}</span>
            {opt.meta !== undefined && opt.meta !== null && (
              <span className="font-mono text-micro tabular-nums text-ink-500">{opt.meta}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Toggle ─────────────────────────────────────────────────────────────── */

/**
 * A switch: the change applies immediately, so there is no Save next to it.
 *
 * @param {object}   props
 * @param {boolean}  props.checked
 * @param {Function} props.onChange     (nextChecked) => void
 * @param {*}        props.label
 * @param {*}        [props.description] one line on why this matters
 */
export function Toggle({ checked = false, onChange, label, description, disabled = false, id, className = '' }) {
  const auto = useId();
  const switchId = id || `toggle-${auto}`;
  const labelId = `${switchId}-label`;
  const descId = `${switchId}-desc`;
  const on = Boolean(checked);

  return (
    <div className={cx('flex items-start gap-3', className)}>
      <button
        type="button"
        id={switchId}
        role="switch"
        aria-checked={on}
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : 'Toggle'}
        aria-describedby={description ? descId : undefined}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!on)}
        /* A pill is the only shape that reads as a switch; the 4/6/8/12 radius
           scale describes boxes, not this. */
        className={cx(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full border border-transparent transition-colors duration-fast',
          disabled ? 'cursor-not-allowed bg-surface-2' : on ? 'bg-brand' : 'bg-surface-3',
        )}
      >
        <span
          className={cx(
            'absolute left-0.5 top-0.5 h-4 w-4 rounded-full transition-transform duration-fast',
            on ? 'translate-x-4' : 'translate-x-0',
            disabled ? 'bg-surface-3 border border-line' : on ? 'bg-brand-fg' : 'bg-surface border border-line-strong',
          )}
        />
      </button>

      {(label || description) && (
        <span className="min-w-0">
          {label && (
            <label
              id={labelId}
              htmlFor={switchId}
              className={cx(
                'block cursor-pointer text-body font-medium',
                disabled ? 'text-ink-400' : 'text-ink-900',
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <span id={descId} className="mt-0.5 block text-micro text-ink-500">
              {description}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/* ─── SearchInput ────────────────────────────────────────────────────────── */

/**
 * A text filter. Controlled and un-debounced on purpose: the caller owns the
 * debounce because only the caller knows whether the keystroke costs a request.
 *
 * @param {object}   props
 * @param {string}   props.value
 * @param {Function} props.onChange   (nextValue) => void, per keystroke
 * @param {Function} [props.onClear]  falls back to onChange('')
 * @param {string}   [props.label]    rendered above the field when supplied
 */
export function SearchInput({
  value = '',
  onChange,
  placeholder = 'Search',
  onClear,
  autoFocus = false,
  id,
  label,
  className = '',
}) {
  const auto = useId();
  const inputId = id || `search-${auto}`;
  const text = value ?? '';
  const hasText = String(text).length > 0;

  const clear = () => {
    if (onClear) onClear();
    else if (onChange) onChange('');
  };

  return (
    <div className={cx('min-w-0', className)}>
      <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
      <div className="relative">
        <i
          className="fas fa-magnifying-glass pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-micro text-ink-400"
          aria-hidden="true"
        />
        <input
          id={inputId}
          type="text"
          value={text}
          autoFocus={autoFocus}
          placeholder={placeholder}
          aria-label={label ? undefined : placeholder}
          onChange={(event) => onChange && onChange(event.target.value)}
          onKeyDown={(event) => {
            // Escape clears rather than closing anything: this input is often the
            // only thing standing between the user and the full result set.
            if (event.key === 'Escape' && hasText) {
              event.preventDefault();
              clear();
            }
          }}
          className={cx(
            FIELD,
            'h-[34px] w-full pl-8 text-label placeholder:text-ink-500',
            hasText ? 'pr-8' : 'pr-2.5',
          )}
        />
        {hasText && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-ink-500 hover:bg-surface-2 hover:text-ink-900"
          >
            <i className="fas fa-xmark text-micro" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── NumberField ────────────────────────────────────────────────────────── */

/**
 * A bounded number — an SLA in minutes, a target as a percentage.
 *
 * An emptied field reports `null`, never 0: a blank SLA box means "not set", and
 * saving it as zero would put every call in breach. Clamping happens on blur so
 * typing "15" into a field with a floor of 10 is not fought at the first digit.
 *
 * @param {object}   props
 * @param {number|null} props.value
 * @param {Function} props.onChange  (nextNumber|null) => void
 * @param {string}   [props.suffix]  a unit, e.g. "min" or "%"
 */
export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  label,
  id,
  disabled = false,
  className = '',
}) {
  const auto = useId();
  const inputId = id || `number-${auto}`;
  const shown = typeof value === 'number' && Number.isFinite(value) ? String(value) : '';

  const emit = (raw, { clamp = false } = {}) => {
    if (!onChange) return;
    if (raw === '') {
      onChange(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return; // mid-typing states like "1e" — hold the last value
    let out = n;
    if (clamp) {
      if (typeof min === 'number') out = Math.max(min, out);
      if (typeof max === 'number') out = Math.min(max, out);
    }
    onChange(out);
  };

  return (
    <div className={cx('min-w-0', className)}>
      <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
      <div className="relative">
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          value={shown}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label={label ? undefined : suffix || 'Number'}
          onChange={(event) => emit(event.target.value)}
          onBlur={(event) => emit(event.target.value, { clamp: true })}
          className={cx(
            FIELD,
            'h-[34px] w-full pl-2.5 text-label font-mono tabular-nums',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-400',
            suffix ? 'pr-10' : 'pr-2.5',
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-micro text-ink-500">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── TextField / TextArea ───────────────────────────────────────────────── */

/**
 * Everything a field owes the user besides the box: its name, whether it is
 * required, why it was rejected, and how close it is to a limit.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Settings screens between them hand-rolled the input recipe about thirty
 * times — `border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-300`
 * and a dozen near-misses of it — so radius, ring colour and height had drifted
 * apart between two fields sitting in the same form. More importantly none of
 * them wired the error to the input: the red text sat in a sibling `<p>` that no
 * screen reader was ever told about, so a rejected form was silent.
 *
 * `error` here sets `aria-invalid`, links the message with `aria-describedby`,
 * and gives it `role="alert"`. That is the whole reason to have the wrapper.
 */
function FieldFrame({ inputId, label, required, error, hint, counter, children, className }) {
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  return (
    <div className={cx('min-w-0', className)}>
      {(label || counter) && (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          {label ? (
            <label htmlFor={inputId} className="block text-label font-medium text-ink-700">
              {label}
              {required ? (
                <span className="ml-0.5 text-bad-text" aria-hidden="true">
                  *
                </span>
              ) : null}
              {hint ? <span className="ml-1 font-normal text-ink-500">{hint}</span> : null}
            </label>
          ) : (
            <span />
          )}
          {counter ? (
            <span className="shrink-0 font-mono text-micro tabular-nums text-ink-500">
              {counter}
            </span>
          ) : null}
        </div>
      )}

      {children({ errorId: error ? errorId : undefined, hintId: hint ? hintId : undefined })}

      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-micro text-bad-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** A field that is showing an error says so in its border, not only in the text. */
const invalidRing = (error) =>
  error ? 'border-bad-line bg-bad-tint' : 'border-line-strong bg-surface';

/**
 * A single-line text input.
 *
 * @param {object}   props
 * @param {string}   props.value
 * @param {Function} props.onChange     (nextValue) => void
 * @param {string}   [props.label]      visible label, also the accessible name
 * @param {string}   [props.hint]       a quiet aside after the label
 * @param {string}   [props.error]      message; also marks the input invalid
 * @param {string}   [props.counter]    e.g. "142/1500", right-aligned on the label row
 * @param {boolean}  [props.required]
 * @param {'sm'|'md'|'lg'} [props.size]
 * @param {*}        [props.iconLeft]   FA class string or a node
 */
export function TextField({
  value = '',
  onChange,
  label,
  hint,
  error,
  counter,
  required = false,
  placeholder,
  size = 'md',
  iconLeft,
  disabled = false,
  autoFocus = false,
  maxLength,
  id,
  className = '',
  inputClassName = '',
  ...rest
}) {
  const auto = useId();
  const inputId = id || `text-${auto}`;
  const step = sizing(size);

  return (
    <FieldFrame
      inputId={inputId}
      label={label}
      hint={hint}
      error={error}
      counter={counter}
      required={required}
      className={className}
    >
      {({ errorId }) => (
        <div className="relative">
          {iconLeft
            ? renderIcon(
                iconLeft,
                'pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-micro text-ink-400'
              )
            : null}
          <input
            id={inputId}
            type="text"
            value={value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            maxLength={maxLength}
            required={required}
            aria-label={label ? undefined : placeholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            onChange={(event) => onChange && onChange(event.target.value)}
            className={cx(
              'rounded-md text-ink-900 transition-colors duration-fast',
              'w-full placeholder:text-ink-500',
              'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-400',
              step.box,
              iconLeft ? 'pl-8 pr-2.5' : step.px,
              invalidRing(error),
              inputClassName
            )}
            {...rest}
          />
        </div>
      )}
    </FieldFrame>
  );
}

/**
 * A multi-line text input.
 *
 * `resize-none` because a user-dragged textarea inside a card breaks the card's
 * layout and the height never survives a re-render anyway; pick `rows` instead.
 *
 * @param {object}   props
 * @param {string}   props.value
 * @param {Function} props.onChange  (nextValue) => void
 * @param {number}   [props.rows]
 * @param {boolean}  [props.mono]    for prompts, where whitespace is meaningful
 */
export function TextArea({
  value = '',
  onChange,
  label,
  hint,
  error,
  counter,
  required = false,
  placeholder,
  rows = 3,
  mono = false,
  disabled = false,
  autoFocus = false,
  maxLength,
  id,
  className = '',
  ...rest
}) {
  const auto = useId();
  const inputId = id || `textarea-${auto}`;

  return (
    <FieldFrame
      inputId={inputId}
      label={label}
      hint={hint}
      error={error}
      counter={counter}
      required={required}
      className={className}
    >
      {({ errorId }) => (
        <textarea
          id={inputId}
          rows={rows}
          value={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          maxLength={maxLength}
          required={required}
          aria-label={label ? undefined : placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          onChange={(event) => onChange && onChange(event.target.value)}
          className={cx(
            'w-full resize-none rounded-md px-2.5 py-2 leading-relaxed text-ink-900',
            'transition-colors duration-fast placeholder:text-ink-500',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-400',
            mono ? 'font-mono text-micro' : 'text-label',
            invalidRing(error)
          )}
          {...rest}
        />
      )}
    </FieldFrame>
  );
}
